/**
 * RegenerateSnapshotModal — preview + tweak the frozen input snapshot before
 * kicking off a free regenerate on My Unlocked Reports.
 *
 * Buyer sees every input that materially drives the calculation, can edit any
 * of them inline, and hits Regenerate. The backend recomputes with the merged
 * inputs and overwrites `input_snapshot` / `result_snapshot`; the version id
 * stays the same, no credit is charged.
 *
 * Fields shown match `engine._CALC_FIELDS` in the backend — those are the ones
 * that actually change the number the buyer sees on the report.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, RotateCw, Loader2, AlertTriangle, TrendingDown, TrendingUp, Minus, FileDown, Mail, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { getLocale, tr, useT } from "@/lib/i18n";
import { toast } from "sonner";
import { comparisonValue } from "@/lib/comparison";
import { num, money as moneyFmt, pct as pctFmt, unit, perUnit } from "@/lib/numfmt";

// Launch D (Jun 2026): the scenarios one-pager is English-only and says "vs diesel" on every grid
// type, so its download, email and lender note are hidden until the v3 rebuild (POST-LAUNCH).
const SCENARIOS_PDF_AT_LAUNCH = false;

const LABEL_FR = {
  Site: "Site", Sector: "Secteur", Country: "Pays", "Altitude (m)": "Altitude (m)", "Ambient temp (°C)": "Température ambiante (°C)",
  Load: "Charge", "Peak load (kW)": "Pointe de charge (kW)", "Daily energy (kWh)": "Énergie journalière (kWh)",
  "Load factor (0–1)": "Facteur de charge (0–1)", "Solar yield (kWh/m²/day)": "Irradiation solaire (kWh/m²/jour)",
  "Grid & priorities": "Réseau et priorités", Grid: "Réseau", "Outage hours/day": "Heures de coupure/jour",
  Ownership: "Mode de détention", Priority: "Priorité", "Fuel price ($/L)": "Prix du carburant ($/L)",
  "Thermal — gas": "Thermique — gaz", "Delivered gas price ($/GJ)": "Prix du gaz livré ($/GJ)",
  "Best case": "Meilleur cas", Base: "Base", Stress: "Stress",
  "Fuel −20% · Outage −1 h · Load −10%": "Carburant −20\u00a0% · Coupures −1\u00a0h · Charge −10\u00a0%",
  "Fuel +50% · Outage +2 h · Load +20%": "Carburant +50\u00a0% · Coupures +2\u00a0h · Charge +20\u00a0%",
  " · Gas −25%": " · Gaz −25\u00a0%", " · Gas +25%": " · Gaz +25\u00a0%", "Reset to the frozen snapshot": "Revenir à l'instantané figé",
  "Diesel price → LCOE (blended)": "Prix du diesel → LCOE (mixte)", "Solar yield → LCOE (blended)": "Irradiation solaire → LCOE (mixte)",
  "Gas price → LCOE (blended)": "Prix du gaz → LCOE (mixte)", LCOE: "LCOE", "Total CAPEX": "CAPEX total",
  Payback: "Retour sur investissement", "Yr-1 savings vs diesel": "Économies A1 vs diesel", "Renewable share": "Part renouvelable",
  "Gas fuel cost / yr": "Coût du gaz / an", "Gas $/GJ": "Gaz $/GJ", "Diesel $/L": "Diesel $/L", "Solar kWh/m²/d": "Solaire kWh/m²/j",
};
const L = (s) => (getLocale() === "fr" && s ? LABEL_FR[s] ?? s : s);

// Grouped presentation of the calc fields so the modal isn't a wall of inputs.
// Every field is optional to edit — untouched fields fall back to the snapshot.
//
// iter44g — the Thermal group is only rendered when the snapshot carries a
// `thermal_selection` (see `visibleGroups` below). Its `gas_price_per_gj`
// slider is a VIRTUAL flat field that we translate into a nested
// `thermal_selection.fuel_gas.delivered_price_per_gj` in the payload builder
// — kept flat here so dirtyKeys / patch() / presets keep working uniformly.
const FIELD_GROUPS = [
  {
    label: "Site",
    fields: [
      { key: "sector",       label: "Sector",        type: "text" },
      { key: "country",      label: "Country",       type: "text" },
      { key: "altitude_m",   label: "Altitude (m)",  type: "number" },
      { key: "ambient_c",    label: "Ambient temp (°C)", type: "number" },
    ],
  },
  {
    label: "Load",
    fields: [
      { key: "peak_kw",      label: "Peak load (kW)", type: "number" },
      // Slider gives buyers a "scrub through scenarios" feel; range is
      // 0.25×–4× the frozen value so the endpoints span most real-world
      // sensitivity questions without spilling into engine-crashing territory.
      { key: "day_kwh",      label: "Daily energy (kWh)", type: "slider",
        rangeFactor: [0.25, 4],   fmt: (v) => `${unit(Math.round(v), "kWh", 0)}` },
      { key: "load_factor",  label: "Load factor (0–1)", type: "number", step: "0.01" },
      // iter45aq — `load_pattern` is deliberately NOT offered here, even
      // though it is one of the backend calc fields this group mirrors. As
      // free text it was the last live route to the four shape ids the
      // engine honours but the regime-families gate has not cleared
      // (`24/7_flat` carries a structural load-factor floor of 1.0, so a
      // selected factor below 1.0 is silently ignored). The backend refuses
      // them at the boundary; re-adding a control here would only surface a
      // 422. Load factor above is the field that moves the model.
      // iter44k — solar-yield sensitivity slider (kWh/m²/day). Tight range
      // (0.7×–1.3× snapshot value) mirrors realistic P10–P90 dispersion;
      // the LCOE-vs-yield mini-chart lands under it via miniChartProps.
      { key: "irradiance",   label: "Solar yield (kWh/m²/day)", type: "slider",
        rangeFactor: [0.7, 1.3], step: 0.1, fmt: (v) => `${unit(v, "kWh/m²/day", 1, 1)}` },
    ],
  },
  {
    label: "Grid & priorities",
    fields: [
      { key: "grid_status",  label: "Grid",           type: "text" },
      // Outage hours has a natural absolute range (0–24) so we use it directly
      // instead of a factor of the frozen value.
      { key: "outage_hours", label: "Outage hours/day", type: "slider",
        range: [0, 24], step: 0.5, fmt: (v) => `${unit(v, "h/day", 1, 1)}` },
      { key: "ownership",    label: "Ownership",      type: "text" },
      { key: "priority",     label: "Priority",       type: "text" },
      { key: "fuel_price",   label: "Fuel price ($/L)", type: "slider",
        rangeFactor: [0.5, 3], step: 0.05, fmt: (v) => `${perUnit(v, "L", 2)}` },
    ],
  },
  {
    label: "Thermal — gas",
    thermalOnly: true,
    fields: [
      // Delivered gas price is the single most-asked lender-side sensitivity
      // when a project has gas-fired generation. Range 0.5×–2× frozen price
      // brackets the Best −25% / Stress +25% preset within a wider scrub band
      // so buyers can explore beyond the presets if they want to.
      { key: "gas_price_per_gj", label: "Delivered gas price ($/GJ)", type: "slider",
        rangeFactor: [0.5, 2], step: 0.05, fmt: (v) => `${perUnit(v, "GJ", 2)}` },
    ],
  },
];

const CALC_KEYS = FIELD_GROUPS.flatMap((g) => g.fields.map((f) => f.key));

export const RegenerateSnapshotModal = ({ open, versionId, onClose, onDone }) => {
  useT();
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);          // {project_name, version, unlocked_at}
  const [original, setOriginal] = useState({});    // frozen snapshot inputs
  const [baselineSummary, setBaselineSummary] = useState(null); // snapshot's headline numbers
  const [previewSummary, setPreviewSummary] = useState(null);   // dry-run result after edits
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [edits, setEdits] = useState({});          // buyer's overrides
  const [busy, setBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  // iter44u — free-text cover note prepended to the scenarios PDF (above
  // the metric table) and echoed in the email body when Emailing.
  const [coverNote, setCoverNote] = useState("");
  // iter44w — Ready-to-use lender-note templates. Buyers pick one to
  // pre-fill the textarea so they don't stare at a blank field; keys /
  // copy tuned with FEV to cover the most-common banker-facing use cases.
  const NOTE_TEMPLATES = [
    { id: "senior_debt",  label: "For senior debt review",
      body: "Prepared for senior debt review. Please read the Base column first; the Stress column bakes a 25 % fuel-price uplift and a two-hour outage extension. Debt service coverage should hold through both." },
    { id: "grant",        label: "For grant application",
      body: "Prepared as a supporting artifact for a grant application. Best case reflects the concessional-tariff scenario; Base is the go-decision reference. CO₂ figures use the same coefficients as the ESG section of the full report." },
    { id: "internal",     label: "For internal review",
      body: "Internal review pack. Base is the current engineering assumption; Best and Stress bracket the sensitivity range we're prepared to defend at the investment committee." },
    { id: "insurance",    label: "For insurance underwriting",
      body: "Prepared for insurance underwriting. Please note the Stress column includes a fuel-price shock plus a load surge — indicative of the worst-case operating envelope for coverage sizing." },
    { id: "gc_ppp",       label: "For general contractor / EPC bid",
      body: "Reference case for EPC bid. Base column reflects our target contracting envelope; CAPEX total assumes turnkey delivery with a bankable performance guarantee." },
  ];
  const [gasCurve, setGasCurve] = useState(null); // { points, baseline_price_per_gj, min, max }
  const [dieselCurve, setDieselCurve] = useState(null); // iter44j — twin for the fuel_price slider
  const [solarCurve, setSolarCurve] = useState(null);  // iter44k — resource sensitivity under irradiance
  const [overlayMode, setOverlayMode] = useState(false); // iter44l — combine all 3 curves on one chart
  const previewTimer = useRef(null);

  useEffect(() => {
    if (!open || !versionId) return;
    setLoading(true); setEdits({}); setMeta(null); setOriginal({});
    setBaselineSummary(null); setPreviewSummary(null); setPreviewError(null);
    setGasCurve(null); setDieselCurve(null); setSolarCurve(null);
    setCoverNote("");
    api.get(`/report-versions/${versionId}/inputs`).then(({ data }) => {
      setMeta({
        project_name: data.project_name || "Untitled",
        version: data.version,
        unlocked_at: (data.unlocked_at || "").slice(0, 10),
        regenerated_at: (data.regenerated_at || "").slice(0, 10),
      });
      // iter44g — surface the nested delivered_price_per_gj as a virtual flat
      // key so the gas-price slider participates in dirty-detection, presets,
      // and preview payload just like any other slider. The raw nested
      // `thermal_selection` stays on `original` so the payload builder can
      // reconstruct the full object without losing chp/ambient/site_thermal.
      const snap = data.input_snapshot || {};
      const gasPrice = snap?.thermal_selection?.fuel_gas?.delivered_price_per_gj;
      if (gasPrice != null) snap.gas_price_per_gj = gasPrice;
      setOriginal(snap);
      setBaselineSummary(data.summary || null);
      // iter44h — fire-and-forget fetch of the $/GJ vs LCOE curve so the
      // gas-price slider can render a mini-chart. Solar-only snapshots
      // return 409 → gasCurve stays null → chart hides itself.
      if (snap?.thermal_selection) {
        api.get(`/report-versions/${versionId}/gas-price-curve`)
          .then(({ data: c }) => setGasCurve(c))
          .catch(() => setGasCurve(null));
      }
      // iter44j — diesel twin. Fires whenever the snapshot has a positive
      // diesel `fuel_price` (works even on solar-only projects — the
      // baseline diesel price still matters for the vs-diesel savings
      // curve). Returns 409 on snapshots without a fuel_price.
      if (Number(snap?.fuel_price) > 0) {
        api.get(`/report-versions/${versionId}/diesel-price-curve`)
          .then(({ data: c }) => setDieselCurve(c))
          .catch(() => setDieselCurve(null));
      }
      // iter44k — solar yield curve. Fires whenever the snapshot has a
      // positive irradiance. Returns 409 when irradiance is missing
      // (the wizard defaults from country data don't land on
      // input_snapshot, so this genuinely only shows when the buyer
      // has an explicit value on file).
      if (Number(snap?.irradiance) > 0) {
        api.get(`/report-versions/${versionId}/solar-yield-curve`)
          .then(({ data: c }) => setSolarCurve(c))
          .catch(() => setSolarCurve(null));
      }
    }).catch(() => {
      toast.error(tr("Could not load the input snapshot", "Impossible de charger l'instantané des données"));
      onClose?.();
    }).finally(() => setLoading(false));
  }, [open, versionId, onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  // ── Derived state (safe to compute before the early-return since it doesn't
  //    use any hooks) — the debounced preview effect below reads these. ──
  const dirtyKeys = Object.keys(edits).filter((k) => {
    const v = edits[k];
    return v !== undefined && v !== "" && String(v) !== String(original[k] ?? "");
  });
  const hasEdits = dirtyKeys.length > 0;
  const hasThermal = Boolean(original?.thermal_selection);

  // iter44g — Translate the buyer's flat edits into the payload the backend
  // /preview and /regenerate endpoints expect. The virtual `gas_price_per_gj`
  // key is expanded into a full nested `thermal_selection` object so the
  // engine sees a well-formed thermal spec (and chp / ambient / site_thermal
  // survive intact).
  const buildOverride = () => {
    if (!hasEdits) return {};
    const out = {};
    for (const k of dirtyKeys) {
      if (k === "gas_price_per_gj") {
        const ts = original.thermal_selection || {};
        out.thermal_selection = {
          ...ts,
          fuel_gas: { ...(ts.fuel_gas || {}), delivered_price_per_gj: Number(edits[k]) },
        };
      } else {
        out[k] = edits[k];
      }
    }
    return out;
  };

  // Debounced dry-run preview whenever the buyer has edits — 500 ms lag so a
  // burst of keystrokes only hits the engine once.
  useEffect(() => {
    if (!open || loading) return;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    if (!hasEdits) {
      setPreviewSummary(null); setPreviewError(null); setPreviewLoading(false);
      return;
    }
    const override = buildOverride();
    setPreviewLoading(true); setPreviewError(null);
    previewTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.post(`/report-versions/${versionId}/preview`, { input_override: override });
        setPreviewSummary(data.summary || null);
      } catch (e) {
        setPreviewError(e?.response?.data?.detail || tr("Preview failed", "Échec de l'aperçu"));
        setPreviewSummary(null);
      } finally { setPreviewLoading(false); }
    }, 500);
    return () => clearTimeout(previewTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edits, versionId, open, loading]);

  const patch = (key, raw, type) => {
    let v = raw;
    if (type === "number" && raw !== "") {
      const n = Number(raw);
      if (!Number.isNaN(n)) v = n;
    }
    setEdits((s) => ({ ...s, [key]: v }));
  };

  // Early-return AFTER all hooks — React's rules-of-hooks require unconditional
  // hook order across renders.
  if (!open) return null;

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const body = { language: getLocale() };
      if (hasEdits) {
        // Only send keys the buyer actually changed — the backend merges these
        // on top of the frozen snapshot before recomputing. `buildOverride`
        // also translates the virtual `gas_price_per_gj` slider into the
        // nested `thermal_selection` shape the engine expects.
        body.input_override = buildOverride();
      }
      const { data } = await api.post(`/report-versions/${versionId}/regenerate`, body);
      toast.success(
        hasEdits
          ? tr(`Report version ${meta?.version} regenerated with your edits`, `Version ${meta?.version} du rapport régénérée avec vos modifications`)
          : tr(`Report version ${meta?.version} regenerated from saved snapshot`, `Version ${meta?.version} du rapport régénérée à partir de l'instantané enregistré`)
      );
      onDone?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Regenerate failed", "Échec de la régénération"));
    } finally { setBusy(false); }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: "rgba(15,23,42,0.55)" }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}
      data-testid="regen-modal"
    >
      <div className="rounded-lg shadow-2xl w-full max-w-4xl flex flex-col overflow-hidden"
           style={{ background: "var(--white)", maxHeight: "88vh", border: "1px solid var(--hairline)" }}>
        <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: "1px solid var(--hairline)" }}>
          <div>
            <div className="text-sm font-semibold" style={{ color: "var(--char)" }}>
              {tr("Preview snapshot & regenerate", "Aperçu de l'instantané et régénération")}
            </div>
            {meta && (
              <div className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>
                {meta.project_name} · v{meta.version} · {tr("originally unlocked", "déverrouillé initialement le")} {meta.unlocked_at}
              </div>
            )}
          </div>
          <button onClick={onClose} disabled={busy} className="p-1" style={{ color: "var(--muted)" }} data-testid="regen-close">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="text-center py-10" style={{ color: "var(--muted)" }}>
              <Loader2 size={22} className="animate-spin inline" /> {tr("Loading snapshot…", "Chargement de l'instantané…")}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="md:col-span-2">
              <p className="text-xs mb-3" style={{ color: "var(--muted)" }}>
                {tr("These are the inputs frozen at unlock time. Leave everything alone to reproduce the original report exactly, or tweak any field to run a fresh calculation. This is free and does not consume a report credit.", "Voici les données figées au moment du déverrouillage. Ne modifiez rien pour reproduire exactement le rapport d'origine, ou ajustez un champ pour lancer un nouveau calcul. C'est gratuit et ne consomme aucun crédit de rapport.")}
              </p>
              <ScenarioPresets original={original} edits={edits} setEdits={setEdits} hasThermal={hasThermal} />
              {/* iter44l — Combined sensitivity overlay. Buyers toggle
                   between per-slider mini-charts (default) and one shared
                   overlay chart so they can compare fuel vs resource
                   exposure at a glance. Toggle only visible when at
                   least one curve has loaded. */}
              {(gasCurve || dieselCurve || solarCurve) && (
                <div className="mb-4" data-testid="regen-overlay-block">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--muted)" }}>
                      {tr("Sensitivity curves", "Courbes de sensibilité")}
                    </div>
                    <label className="text-[11px] flex items-center gap-1.5 cursor-pointer" style={{ color: "var(--char)" }}>
                      <input type="checkbox" checked={overlayMode}
                              onChange={(e) => setOverlayMode(e.target.checked)}
                              className="accent-[var(--brand-green)]"
                              data-testid="regen-overlay-toggle" />
                      <span>{tr("Overlay all three on one chart", "Superposer les trois sur un seul graphique")}</span>
                    </label>
                  </div>
                  {overlayMode && (
                    <CurveOverlayChart
                      gasCurve={gasCurve}
                      dieselCurve={dieselCurve}
                      solarCurve={solarCurve}
                      gasCurrent={edits.gas_price_per_gj ?? original.gas_price_per_gj}
                      dieselCurrent={edits.fuel_price ?? original.fuel_price}
                      solarCurrent={edits.irradiance ?? original.irradiance}
                    />
                  )}
                </div>
              )}
              {FIELD_GROUPS.filter((g) => !g.thermalOnly || hasThermal).map((group) => (
                <div key={L(group.label)} className="mb-4">
                  <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--muted)" }}>
                    {L(group.label)}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {group.fields.map((f) => {
                      const orig = original[f.key];
                      const val = edits[f.key] !== undefined ? edits[f.key] : (orig ?? "");
                      const dirty = dirtyKeys.includes(f.key);
                      if (f.type === "slider") {
                        // iter44h/j/k — inline mini-chart under the three
                        // sensitivity sliders. Gas → $/GJ, diesel → $/L,
                        // solar → kWh/m²/day. Suppressed entirely in
                        // overlay mode (iter44l) so we don't render the
                        // same curve twice.
                        let chart = null, chartProps = null;
                        if (!overlayMode) {
                          if (f.key === "gas_price_per_gj") {
                            chart = gasCurve;
                          } else if (f.key === "fuel_price") {
                            chart = dieselCurve;
                            chartProps = {
                              label: "Diesel price → LCOE (blended)",
                              priceUnit: "/L",
                              xKey: "price_per_l",
                              baselineKey: "baseline_price_per_l",
                            };
                          } else if (f.key === "irradiance") {
                            chart = solarCurve;
                            chartProps = {
                              label: "Solar yield → LCOE (blended)",
                              priceUnit: " kWh/m²/d",
                              pricePrefix: "",
                              xKey: "irradiance_kwh_m2_day",
                              baselineKey: "baseline_irradiance",
                            };
                          }
                        }
                        return (
                          <SliderField
                            key={f.key}
                            field={f}
                            original={orig}
                            value={val}
                            dirty={dirty}
                            onChange={(v) => patch(f.key, v, "number")}
                            miniChart={chart}
                            miniChartProps={chartProps}
                          />
                        );
                      }
                      return (
                        <div key={f.key}>
                          <label className="text-[11px] font-medium block mb-0.5" style={{ color: "var(--char)" }}>
                            {L(f.label)}
                            {dirty && <span className="ml-1 px-1.5 py-0.5 text-[9px] font-bold rounded"
                                              style={{ background: "#FEF3C7", color: "#873F00" }}>{tr("edited", "modifié")}</span>}
                          </label>
                          <input
                            type={f.type}
                            step={f.step}
                            value={val}
                            onChange={(e) => patch(f.key, e.target.value, f.type)}
                            placeholder={orig === undefined || orig === null ? tr("not set", "non renseigné") : String(orig)}
                            className="w-full px-2 py-1.5 text-xs rounded"
                            style={{
                              border: `1px solid ${dirty ? "#873F00" : "var(--hairline)"}`,
                              background: dirty ? "#FFF7ED" : "var(--white)",
                              fontFamily: f.type === "number" ? "'JetBrains Mono', ui-monospace, monospace" : undefined,
                            }}
                            data-testid={`regen-input-${f.key}`}
                          />
                          {dirty && orig !== undefined && orig !== null && (
                            <div className="text-[10px] mt-0.5" style={{ color: "var(--muted)" }}>
                              {tr("was", "avant")} <span className="font-mono">{String(orig)}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              </div>

              {/* Diff panel — sticky on desktop so it stays visible while the buyer scrolls the form. */}
              <aside className="md:col-span-1" data-testid="regen-diff-panel">
                <div className="rounded p-3 md:sticky md:top-0"
                       style={{ background: "var(--paper)", border: "1px solid var(--hairline)" }}>
                  <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--muted)" }}>
                    {tr("See what changed", "Ce qui change")}
                  </div>
                  {!hasEdits ? (
                    <div className="text-xs" style={{ color: "var(--muted)" }} data-testid="regen-diff-empty">
                      {tr("Edit any field on the left to preview how the recompute would affect the headline numbers.", "Modifiez un champ à gauche pour voir l'effet du recalcul sur les chiffres clés.")}
                    </div>
                  ) : previewError ? (
                    <div className="text-xs p-2 rounded" style={{ background: "#FDECEC", color: "#7A1F17", border: "1px solid #C0392B" }}
                            data-testid="regen-diff-error">
                      <AlertTriangle size={11} className="inline -mt-0.5" /> {previewError}
                    </div>
                  ) : (
                    <div className="space-y-2" data-testid="regen-diff-metrics">
                      <DiffRow label="LCOE" unit="$/kWh" prec={3}
                                before={baselineSummary?.lcoe_usd_per_kwh}
                                after={previewSummary?.lcoe_usd_per_kwh}
                                loading={previewLoading} betterWhen="lower" />
                      <DiffRow label="Total CAPEX" unit="$" prec={0} money
                                before={baselineSummary?.capex_total}
                                after={previewSummary?.capex_total}
                                loading={previewLoading} betterWhen="lower" />
                      <DiffRow label="Payback" unit="yrs" prec={1}
                                before={comparisonValue(baselineSummary?.simple_payback_years)}
                                after={comparisonValue(previewSummary?.simple_payback_years)}
                                loading={previewLoading} betterWhen="lower" />
                      <DiffRow label="Yr-1 savings vs diesel" unit="$" prec={0} money
                                before={comparisonValue(baselineSummary?.annual_savings_vs_diesel_yr1)}
                                after={comparisonValue(previewSummary?.annual_savings_vs_diesel_yr1)}
                                loading={previewLoading} betterWhen="higher" />
                      <DiffRow label="Renewable share" unit="%" prec={0} pct
                                before={baselineSummary?.renewable_fraction}
                                after={previewSummary?.renewable_fraction}
                                loading={previewLoading} betterWhen="higher" />
                      {hasThermal && (
                        <DiffRow label="Gas fuel cost / yr" unit="$" prec={0} money
                                  before={baselineSummary?.thermal_fuel_cost_usd}
                                  after={previewSummary?.thermal_fuel_cost_usd}
                                  loading={previewLoading} betterWhen="lower" />
                      )}
                    </div>
                  )}
                </div>
              </aside>
            </div>
          )}
        </div>

        <div className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap" style={{ borderTop: "1px solid var(--hairline)", background: "var(--paper)" }}>
          <div className="text-xs" style={{ color: hasEdits ? "#873F00" : "var(--muted)" }} data-testid="regen-edits-count">
            {hasEdits ? (
              <><AlertTriangle size={11} className="inline -mt-0.5" /> {getLocale() === "fr" ? `${dirtyKeys.length}\u00a0modification${dirtyKeys.length === 1 ? "" : "s"} · recalcul` : `${dirtyKeys.length} edit${dirtyKeys.length === 1 ? "" : "s"} · will recompute`}</>
            ) : (
              <>{tr("No edits · will restore the original snapshot", "Aucune modification · l'instantané d'origine sera restauré")}</>
            )}
          </div>
          {/* iter44u — Optional cover note. Renders above the metric table
               in the scenarios PDF and is echoed in the email body. Capped
               at 500 chars server-side; input maxLength gives immediate
               feedback. Hidden by default via collapsed height so the
               modal footer stays compact for buyers who don't need it. */}
          {SCENARIOS_PDF_AT_LAUNCH && (
          <details className="w-full order-3 md:order-2 md:w-auto md:flex-1 md:mx-3" data-testid="regen-cover-note-details">
            <summary className="text-[11px] cursor-pointer inline-flex items-center gap-1" style={{ color: "var(--muted)" }}>
              <FileText size={11} />
              Add a note to lender{coverNote ? ` (${coverNote.length}/500)` : ""}
            </summary>
            {/* iter44w — Template dropdown. Selecting a template pre-fills
                 the textarea; picking the empty option leaves whatever the
                 buyer has already typed alone. Select value stays at "" so
                 buyers can re-pick the same template if they cleared it. */}
            <div className="flex items-center gap-2 mt-1.5">
              <select
                value=""
                onChange={(e) => {
                  const t = NOTE_TEMPLATES.find((x) => x.id === e.target.value);
                  if (t) setCoverNote(t.body);
                }}
                className="text-[11px] px-2 py-1 border rounded"
                style={{ borderColor: "var(--rule)", background: "var(--white)", color: "var(--char)" }}
                data-testid="regen-cover-note-template"
              >
                <option value="">— Insert a template…</option>
                {NOTE_TEMPLATES.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
              {coverNote && (
                <button
                  type="button"
                  onClick={() => setCoverNote("")}
                  className="text-[10px] underline"
                  style={{ color: "var(--muted)" }}
                  data-testid="regen-cover-note-clear"
                >
                  clear
                </button>
              )}
            </div>
            <textarea
              rows={2}
              maxLength={500}
              value={coverNote}
              onChange={(e) => setCoverNote(e.target.value)}
              placeholder="e.g. Prepared for Bank X — assume 8 % senior-debt cover, please read Base column first."
              className="w-full mt-1.5 px-2 py-1.5 text-xs rounded border font-sans"
              style={{ borderColor: "var(--rule)", background: "var(--white)", color: "var(--char)" }}
              data-testid="regen-cover-note-input"
            />
            <div className="text-[10px] mt-0.5" style={{ color: "var(--muted)" }}>
              Prints above the metric table in the PDF and appears in the email body.
            </div>
          </details>
          )}
          <div className="flex gap-2 order-2 md:order-3">
            {SCENARIOS_PDF_AT_LAUNCH && (<>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/report-versions/${versionId}/scenarios.pdf${coverNote ? "?cover_note=" + encodeURIComponent(coverNote) : ""}`}
              download
              className="btn-secondary !py-1.5 !px-3 text-xs"
              data-testid="regen-scenarios-pdf"
              title="Download a one-page PDF with Best / Base / Stress side-by-side — for banker reviews"
            >
              <FileDown size={12} /> Send 3 scenarios PDF
            </a>
            <button
              type="button"
              onClick={async () => {
                if (emailBusy) return;
                setEmailBusy(true);
                try {
                  const { data } = await api.post(`/report-versions/${versionId}/scenarios/email`,
                    coverNote ? { cover_note: coverNote } : {});
                  toast.success(`Scenarios PDF emailed to ${data.sent_to}`, {
                    description: "Forward from your inbox to reach your lender.",
                  });
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Could not send email");
                } finally { setEmailBusy(false); }
              }}
              disabled={emailBusy || loading}
              className="btn-secondary !py-1.5 !px-3 text-xs"
              data-testid="regen-scenarios-email"
              title="Send the scenarios PDF to your account email — forward from your inbox to lenders"
            >
              {emailBusy ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
              Email me this PDF
            </button>
            </>)}
            <button onClick={onClose} disabled={busy} className="btn-secondary !py-1.5 !px-3 text-xs" data-testid="regen-cancel">{tr("Cancel", "Annuler")}</button>
            <button
              onClick={submit}
              disabled={busy || loading}
              className="btn-primary !py-1.5 !px-3 text-xs"
              data-testid="regen-submit"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCw size={12} />}
              {hasEdits ? tr("Regenerate with edits", "Régénérer avec les modifications") : tr("Regenerate", "Régénérer")}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// Kept as a named export for the caller — the parent decides when to open it.
export const REGEN_CALC_KEYS = CALC_KEYS;

// ── Scenario preset strip ───────────────────────────────────────────────
// Snaps the sensitivity sliders (day_kwh, outage_hours, fuel_price, and —
// on thermal snapshots — gas_price_per_gj) to common banker-review scenarios
// so buyers can pitch best / base / stress in one modal without dragging
// manually. Each preset merges into the existing `edits` state so non-slider
// fields the buyer had already touched are kept intact; Base clears all
// slider keys back to the snapshot.
//
// iter44g — Gas price nudges (±25%) match the backend `_SCENARIOS` recipe
// in server.py, so what a buyer previews on-screen matches what the
// emailed scenarios PDF prints for lenders.
const SCENARIOS = [
  {
    key: "best",
    label: "Best case",
    hint: "Fuel −20% · Outage −1 h · Load −10%",
    thermalHintExtra: " · Gas −25%",
    apply: (o) => {
      const out = {
        day_kwh:      round(Number(o.day_kwh ?? 0) * 0.9, 0),
        outage_hours: Math.max(0, Number(o.outage_hours ?? 0) - 1),
        fuel_price:   round(Number(o.fuel_price ?? 0) * 0.8, 2),
      };
      if (o.gas_price_per_gj != null) {
        out.gas_price_per_gj = round(Number(o.gas_price_per_gj) * 0.75, 2);
      }
      return out;
    },
  },
  {
    key: "base",
    label: "Base",
    hint: "Reset to the frozen snapshot",
    apply: () => null,  // sentinel — parent clears the slider keys
  },
  {
    key: "stress",
    label: "Stress",
    hint: "Fuel +50% · Outage +2 h · Load +20%",
    thermalHintExtra: " · Gas +25%",
    apply: (o) => {
      const out = {
        day_kwh:      round(Number(o.day_kwh ?? 0) * 1.2, 0),
        outage_hours: Math.min(24, Number(o.outage_hours ?? 0) + 2),
        fuel_price:   round(Number(o.fuel_price ?? 0) * 1.5, 2),
      };
      if (o.gas_price_per_gj != null) {
        out.gas_price_per_gj = round(Number(o.gas_price_per_gj) * 1.25, 2);
      }
      return out;
    },
  },
];
const SLIDER_KEYS_BASE = ["day_kwh", "outage_hours", "fuel_price"];
const SLIDER_KEYS_THERMAL = [...SLIDER_KEYS_BASE, "gas_price_per_gj"];
const round = (v, p) => { const f = Math.pow(10, p); return Math.round(v * f) / f; };

const ScenarioPresets = ({ original, edits, setEdits, hasThermal }) => {
  const SLIDER_KEYS = hasThermal ? SLIDER_KEYS_THERMAL : SLIDER_KEYS_BASE;
  // Which preset (if any) currently matches the buyer's slider values? Lets
  // us highlight the active button so buyers see which scenario is showing.
  const active = (() => {
    const cur = Object.fromEntries(SLIDER_KEYS.map((k) => [k, edits[k] ?? original[k]]));
    for (const s of SCENARIOS) {
      if (s.key === "base") {
        // Base = every slider still at snapshot value (no edits on any slider key)
        if (SLIDER_KEYS.every((k) => edits[k] === undefined || String(edits[k]) === String(original[k] ?? ""))) {
          return "base";
        }
        continue;
      }
      const proj = s.apply(original);
      if (SLIDER_KEYS.every((k) => {
        const projected = proj[k];
        // A key the preset didn't touch (e.g. gas_price on solar-only) matches
        // whenever the buyer hasn't edited it either.
        if (projected === undefined) return edits[k] === undefined;
        return Math.abs(Number(cur[k] ?? 0) - Number(projected)) < 0.01;
      })) {
        return s.key;
      }
    }
    return null;
  })();

  const applyScenario = (s) => {
    const proj = s.apply(original);
    setEdits((e) => {
      const next = { ...e };
      if (proj === null) {
        // Base: clear all slider keys so the sliders snap back to snapshot.
        SLIDER_KEYS.forEach((k) => { delete next[k]; });
      } else {
        Object.entries(proj).forEach(([k, v]) => { next[k] = v; });
      }
      return next;
    });
  };

  return (
    <div className="mb-4" data-testid="regen-scenarios">
      <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--muted)" }}>
        {tr("Scenario presets", "Scénarios prédéfinis")}
      </div>
      <div className="flex gap-2 flex-wrap">
        {SCENARIOS.map((s) => {
          const isActive = active === s.key;
          const hint = (hasThermal && s.thermalHintExtra)
            ? `${L(s.hint)}${L(s.thermalHintExtra)}`
            : L(s.hint);
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => applyScenario(s)}
              className="text-left px-3 py-2 rounded flex-1 min-w-[140px] transition-colors"
              style={{
                background: isActive ? "var(--brand-green)" : "var(--white)",
                color: isActive ? "#fff" : "var(--char)",
                border: `1px solid ${isActive ? "var(--brand-green)" : "var(--hairline)"}`,
              }}
              data-testid={`regen-scenario-${s.key}`}
              aria-pressed={isActive}
            >
              <div className="text-xs font-bold">{L(s.label)}</div>
              <div className="text-[10px] mt-0.5" style={{ color: isActive ? "rgba(255,255,255,0.85)" : "var(--muted)" }}>
                {hint}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ── Slider field ────────────────────────────────────────────────────────
// Native <input type="range"> styled to fit the modal aesthetic. Buyer can
// scrub OR type a value into the small number input on the right; both stay
// in sync. The debounced preview effect (owned by the parent modal) fires on
// every change so the diff panel updates while the buyer is dragging.
const SliderField = ({ field, original, value, dirty, onChange, miniChart, miniChartProps }) => {
  const origN = typeof original === "number" ? original : Number(original) || 0;
  // Range: explicit [min,max] wins; else derive as origN × rangeFactor. For a
  // zero-baseline snapshot (rare), fall back to [0, 100] so the slider still
  // renders something usable.
  let min, max;
  if (Array.isArray(field.range)) {
    [min, max] = field.range;
  } else if (Array.isArray(field.rangeFactor) && origN > 0) {
    min = origN * field.rangeFactor[0];
    max = origN * field.rangeFactor[1];
  } else {
    min = 0;
    max = 100;
  }
  const step = field.step ?? (max - min > 100 ? 10 : (max - min > 10 ? 1 : 0.1));
  const current = typeof value === "number" ? value
                 : (value === "" || value === undefined ? origN : Number(value));
  const clamped = Math.min(max, Math.max(min, current || origN));

  return (
    <div className="col-span-1 md:col-span-2">
      <div className="flex items-center justify-between mb-0.5">
        <label className="text-[11px] font-medium" style={{ color: "var(--char)" }}>
          {L(field.label)}
          {dirty && (
            <span className="ml-1 px-1.5 py-0.5 text-[9px] font-bold rounded"
                     style={{ background: "#FEF3C7", color: "#873F00" }}>{tr("edited", "modifié")}</span>
          )}
        </label>
        <span className="text-[11px] font-mono font-bold"
                 style={{ color: dirty ? "#873F00" : "var(--char)" }}
                 data-testid={`regen-slider-value-${field.key}`}>
          {field.fmt ? field.fmt(clamped) : String(clamped)}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={clamped}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full"
        style={{ accentColor: dirty ? "#873F00" : "var(--brand-green)" }}
        data-testid={`regen-slider-${field.key}`}
      />
      <div className="flex items-center justify-between text-[10px] mt-0.5" style={{ color: "var(--muted)" }}>
        <span>{field.fmt ? field.fmt(min) : min}</span>
        {dirty && original !== undefined && original !== null ? (
          <span>{tr("was", "avant")} <span className="font-mono">{field.fmt ? field.fmt(origN) : String(original)}</span></span>
        ) : (
          <span>{tr("snapshot:", "instantané\u00a0:")} <span className="font-mono">{field.fmt ? field.fmt(origN) : String(original)}</span></span>
        )}
        <span>{field.fmt ? field.fmt(max) : max}</span>
      </div>
      {miniChart && <GasPriceLcoeChart curve={miniChart} currentPrice={clamped} fieldKey={field.key} {...(miniChartProps || {})} />}
    </div>
  );
};

// ── iter44h — Mini "$/GJ vs LCOE" chart ─────────────────────────────────
// Renders the server-computed curve as a thin SVG polyline with a "you are
// here" dot at the current slider position and a dashed reference line at
// the snapshot's baseline price. All numeric — no libraries — so it stays
// under 3 KB and doesn't add a chart dependency.
// ── iter44h/44j — Mini "$/x vs LCOE" chart ──────────────────────────────
// Generic price-vs-LCOE polyline used by both the gas-price and the diesel
// fuel-price sliders. The `curve` prop shape carries an `xKey` telling the
// component which field on each point holds the x-value (price_per_gj or
// price_per_l) and which baseline-* key holds the snapshot reference; the
// price-formatter + section title come from `label`/`priceUnit`.
const GasPriceLcoeChart = ({ curve, currentPrice, fieldKey, label, priceUnit = "/GJ", pricePrefix = "$", priceDp, xKey = "price_per_gj", baselineKey = "baseline_price_per_gj" }) => {
  if (!curve || !Array.isArray(curve.points) || curve.points.length < 2) return null;
  const pts = curve.points.filter((p) => p.lcoe_usd_per_kwh != null);
  if (pts.length < 2) return null;

  // SVG viewport. Fixed width in units — parent scales via CSS width:100%.
  const W = 320, H = 80, PAD_L = 42, PAD_R = 8, PAD_T = 8, PAD_B = 20;
  const xs = pts.map((p) => p[xKey]);
  const ys = pts.map((p) => p.lcoe_usd_per_kwh);
  const xMin = Math.min(...xs), xMax = Math.max(...xs);
  const yMin = Math.min(...ys), yMax = Math.max(...ys);
  const yPad = (yMax - yMin) * 0.15 || 0.005;
  const yLo = Math.max(0, yMin - yPad), yHi = yMax + yPad;
  const X = (x) => PAD_L + (x - xMin) / (xMax - xMin || 1) * (W - PAD_L - PAD_R);
  const Y = (y) => PAD_T + (1 - (y - yLo) / (yHi - yLo || 1)) * (H - PAD_T - PAD_B);

  // Polyline path.
  const path = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${X(p[xKey]).toFixed(1)} ${Y(p.lcoe_usd_per_kwh).toFixed(1)}`).join(" ");

  // Baseline vertical dashed line (snapshot's own price).
  const baseX = X(curve[baselineKey]);
  // Live "you are here" dot — interpolate LCOE at currentPrice from the
  // sampled points so the dot glides smoothly along the curve.
  const clamped = Math.max(xMin, Math.min(xMax, currentPrice));
  let liveY = null;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i], b = pts[i + 1];
    if (clamped >= a[xKey] && clamped <= b[xKey]) {
      const t = (clamped - a[xKey]) / (b[xKey] - a[xKey] || 1);
      liveY = a.lcoe_usd_per_kwh + t * (b.lcoe_usd_per_kwh - a.lcoe_usd_per_kwh);
      break;
    }
  }
  if (liveY == null) liveY = clamped <= xMin ? pts[0].lcoe_usd_per_kwh : pts[pts.length - 1].lcoe_usd_per_kwh;

  const fmtLcoe = (v) => moneyFmt(v, 3, 3);
  // Diesel prices are typically $0.5–3/L — show 2 decimals; gas $/GJ shows 1.
  // Irradiance (kWh/m²/day) uses 1 decimal too. `priceDp` prop overrides.
  const dp = priceDp != null ? priceDp : (priceUnit === "/L" ? 2 : 1);
  const fmtPrice = (v) => `${pricePrefix}${num(v, dp, dp)}${priceUnit}`;
  const title = L(label || "Gas price → LCOE (blended)");
  const aria = label ? `${label} curve` : "Gas price versus LCOE curve";

  return (
    <div className="mt-2" data-testid={`regen-chart-${fieldKey}`}>
      <div className="text-[9px] font-bold uppercase tracking-wider mb-0.5" style={{ color: "var(--muted)" }}>
        {title}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="80" style={{ display: "block" }}
            role="img" aria-label={aria}>
        {/* Y-axis labels */}
        <text x={PAD_L - 4} y={PAD_T + 4} fontSize="8" textAnchor="end" fill="var(--muted)" fontFamily="ui-monospace,monospace">{fmtLcoe(yHi)}</text>
        <text x={PAD_L - 4} y={H - PAD_B} fontSize="8" textAnchor="end" fill="var(--muted)" fontFamily="ui-monospace,monospace">{fmtLcoe(yLo)}</text>
        {/* X-axis labels */}
        <text x={PAD_L} y={H - 4} fontSize="8" textAnchor="start" fill="var(--muted)" fontFamily="ui-monospace,monospace">{fmtPrice(xMin)}</text>
        <text x={W - PAD_R} y={H - 4} fontSize="8" textAnchor="end" fill="var(--muted)" fontFamily="ui-monospace,monospace">{fmtPrice(xMax)}</text>
        {/* Frame */}
        <rect x={PAD_L} y={PAD_T} width={W - PAD_L - PAD_R} height={H - PAD_T - PAD_B}
               fill="none" stroke="var(--hairline)" strokeWidth="1" />
        {/* Snapshot baseline reference (dashed) */}
        <line x1={baseX} x2={baseX} y1={PAD_T} y2={H - PAD_B}
               stroke="#B26B25" strokeWidth="1" strokeDasharray="3 3" opacity="0.7" />
        <text x={baseX} y={PAD_T + 8} fontSize="8" textAnchor="middle" fill="#B26B25" fontWeight="bold">{tr("snapshot", "instantané")}</text>
        {/* Curve */}
        <path d={path} fill="none" stroke="var(--brand-green)" strokeWidth="1.8" strokeLinejoin="round" />
        {/* Sample dots */}
        {pts.map((p, i) => (
          <circle key={i} cx={X(p[xKey])} cy={Y(p.lcoe_usd_per_kwh)} r="1.6" fill="var(--brand-green)" />
        ))}
        {/* Live "you are here" dot */}
        <circle cx={X(clamped)} cy={Y(liveY)} r="4" fill="#873F00" stroke="#fff" strokeWidth="1.5"
                 data-testid={`regen-chart-live-${fieldKey}`} />
      </svg>
      <div className="text-[10px] mt-0.5 flex items-center justify-between" style={{ color: "var(--muted)" }}>
        <span>{tr("at", "à")} <span className="font-mono">{fmtPrice(clamped)}</span></span>
        <span>{tr("blended LCOE", "LCOE mixte")} <span className="font-mono font-bold" style={{ color: "#873F00" }}>{fmtLcoe(liveY)}/kWh</span></span>
      </div>
    </div>
  );
};

// ── iter44l — Combined sensitivity overlay ──────────────────────────────
// All three sensitivity curves on one chart with a shared normalized
// x-axis (% deviation from the snapshot baseline). Buyer can compare
// fuel vs resource exposure at a glance without scrolling between the
// three per-slider mini-charts. Each line is coloured distinctly + gets
// its own live "you are here" dot driven by the corresponding slider.
const CurveOverlayChart = ({ gasCurve, dieselCurve, solarCurve,
                             gasCurrent, dieselCurrent, solarCurrent }) => {
  // Build a normalized-x series for each available curve so they can share
  // one x-axis. A curve is skipped entirely if it's null or has <2 usable
  // points (409 from the server, or all-null LCOEs).
  const series = [];
  const normalize = (c, xKey, baselineKey, currentX, label, color) => {
    if (!c || !Array.isArray(c.points)) return null;
    const base = c[baselineKey];
    if (!base) return null;
    const pts = c.points
      .filter((p) => p.lcoe_usd_per_kwh != null)
      .map((p) => ({ nx: p[xKey] / base - 1, lcoe: p.lcoe_usd_per_kwh }));
    if (pts.length < 2) return null;
    // Interpolate LCOE at the buyer's current slider value.
    const nxCur = (Number(currentX) || base) / base - 1;
    let liveY = null;
    const sorted = [...pts].sort((a, b) => a.nx - b.nx);
    if (nxCur <= sorted[0].nx) liveY = sorted[0].lcoe;
    else if (nxCur >= sorted[sorted.length - 1].nx) liveY = sorted[sorted.length - 1].lcoe;
    else {
      for (let i = 0; i < sorted.length - 1; i++) {
        const a = sorted[i], b = sorted[i + 1];
        if (nxCur >= a.nx && nxCur <= b.nx) {
          const t = (nxCur - a.nx) / (b.nx - a.nx || 1);
          liveY = a.lcoe + t * (b.lcoe - a.lcoe);
          break;
        }
      }
    }
    return { label, color, pts: sorted, nxCur, liveY };
  };
  const gas = normalize(gasCurve, "price_per_gj", "baseline_price_per_gj", gasCurrent, L("Gas $/GJ"), "#1E7A54");
  const diesel = normalize(dieselCurve, "price_per_l", "baseline_price_per_l", dieselCurrent, L("Diesel $/L"), "#B26B25");
  const solar = normalize(solarCurve, "irradiance_kwh_m2_day", "baseline_irradiance", solarCurrent, L("Solar kWh/m²/d"), "#1F6BB5");
  [gas, diesel, solar].forEach((s) => { if (s) series.push(s); });
  if (series.length === 0) return null;

  const W = 640, H = 200, PAD_L = 52, PAD_R = 16, PAD_T = 24, PAD_B = 44;
  const allX = series.flatMap((s) => s.pts.map((p) => p.nx));
  const allY = series.flatMap((s) => s.pts.map((p) => p.lcoe));
  const xMin = Math.min(...allX), xMax = Math.max(...allX);
  const yMin = Math.min(...allY), yMax = Math.max(...allY);
  const yPad = (yMax - yMin) * 0.15 || 0.005;
  const yLo = Math.max(0, yMin - yPad), yHi = yMax + yPad;
  const X = (x) => PAD_L + (x - xMin) / (xMax - xMin || 1) * (W - PAD_L - PAD_R);
  const Y = (y) => PAD_T + (1 - (y - yLo) / (yHi - yLo || 1)) * (H - PAD_T - PAD_B);
  const fmtLcoe = (v) => moneyFmt(v, 3, 3);
  const fmtPct = (v) => `${(v * 100 > 0 ? "+" : "")}${pctFmt(v * 100, 0)}`;

  return (
    <div className="rounded p-3" data-testid="regen-chart-overlay"
          style={{ background: "var(--paper)", border: "1px solid var(--hairline)" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="200" style={{ display: "block" }}
            role="img" aria-label="Combined sensitivity overlay">
        {/* Y-axis labels */}
        <text x={PAD_L - 6} y={PAD_T + 4} fontSize="9" textAnchor="end" fill="var(--muted)" fontFamily="ui-monospace,monospace">{fmtLcoe(yHi)}</text>
        <text x={PAD_L - 6} y={H - PAD_B} fontSize="9" textAnchor="end" fill="var(--muted)" fontFamily="ui-monospace,monospace">{fmtLcoe(yLo)}</text>
        <text x={PAD_L - 6} y={PAD_T - 8} fontSize="8" textAnchor="end" fill="var(--muted)">LCOE $/kWh</text>
        {/* X-axis labels — anchored at 0% (snapshot baseline) */}
        <text x={PAD_L} y={H - PAD_B + 14} fontSize="9" textAnchor="start" fill="var(--muted)" fontFamily="ui-monospace,monospace">{fmtPct(xMin)}</text>
        <text x={X(0)} y={H - PAD_B + 14} fontSize="9" textAnchor="middle" fill="#B26B25" fontFamily="ui-monospace,monospace" fontWeight="bold">{tr("snapshot", "instantané")}</text>
        <text x={W - PAD_R} y={H - PAD_B + 14} fontSize="9" textAnchor="end" fill="var(--muted)" fontFamily="ui-monospace,monospace">{fmtPct(xMax)}</text>
        <text x={(PAD_L + W - PAD_R) / 2} y={H - PAD_B + 30} fontSize="8" textAnchor="middle" fill="var(--muted)">{tr("deviation from snapshot baseline", "écart par rapport à l'instantané")}</text>
        {/* Frame */}
        <rect x={PAD_L} y={PAD_T} width={W - PAD_L - PAD_R} height={H - PAD_T - PAD_B}
               fill="none" stroke="var(--hairline)" strokeWidth="1" />
        {/* Zero-deviation reference (dashed amber) — snapshot baseline */}
        <line x1={X(0)} x2={X(0)} y1={PAD_T} y2={H - PAD_B}
               stroke="#B26B25" strokeWidth="1" strokeDasharray="3 3" opacity="0.7" />
        {/* Each series */}
        {series.map((s) => {
          const path = s.pts.map((p, i) => `${i === 0 ? "M" : "L"} ${X(p.nx).toFixed(1)} ${Y(p.lcoe).toFixed(1)}`).join(" ");
          return (
            <g key={s.label}>
              <path d={path} fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round" />
              {s.pts.map((p, i) => (
                <circle key={i} cx={X(p.nx)} cy={Y(p.lcoe)} r="1.6" fill={s.color} />
              ))}
              {/* Live "you are here" dot per series */}
              <circle cx={X(s.nxCur)} cy={Y(s.liveY)} r="4.5" fill={s.color} stroke="#fff" strokeWidth="1.8" />
            </g>
          );
        })}
      </svg>
      {/* Legend + live readouts */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-[10px]" data-testid="regen-chart-overlay-legend">
        {series.map((s) => (
          <div key={s.label} className="flex items-center gap-1.5">
            <span className="inline-block" style={{ width: 10, height: 3, background: s.color, borderRadius: 1 }} />
            <span style={{ color: "var(--char)" }}>{s.label}</span>
            <span style={{ color: "var(--muted)" }}>
              at {fmtPct(s.nxCur)} → <span className="font-mono font-bold" style={{ color: s.color }}>{fmtLcoe(s.liveY)}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};


// ── Diff panel row ───────────────────────────────────────────────────────
// Renders a single before → after metric with a coloured delta chip. `betterWhen`
// controls colour (green for improvement / red for regression). `money`/`pct`
// format the raw number for display.
const _fmt = (v, { prec = 1, money = false, pct = false } = {}) => {
  if (v == null || Number.isNaN(v)) return "—";
  if (pct) return pctFmt(v * 100, prec);
  if (money) return moneyFmt(v, prec);
  return num(v, prec, prec);
};

const DiffRow = ({ label, unit, before, after, loading, prec, money, pct, betterWhen = "lower" }) => {
  const hasBefore = before != null;
  const hasAfter = after != null && !loading;
  let delta = null, deltaPct = null;
  if (hasBefore && hasAfter) {
    delta = after - before;
    deltaPct = before !== 0 ? (delta / Math.abs(before)) * 100 : null;
  }
  const isImprove = delta != null && ((betterWhen === "lower" && delta < 0) || (betterWhen === "higher" && delta > 0));
  const isFlat = delta != null && Math.abs(delta) < (Math.abs(before || 1) * 0.001);
  const chipColor = isFlat ? "var(--muted)" : (isImprove ? "var(--brand-green)" : "#C0392B");
  const chipBg = isFlat ? "var(--paper)" : (isImprove ? "#E4F3E7" : "#FDECEC");
  const Icon = isFlat ? Minus : (isImprove
    ? (betterWhen === "lower" ? TrendingDown : TrendingUp)
    : (betterWhen === "lower" ? TrendingUp : TrendingDown));

  return (
    <div className="flex items-baseline justify-between gap-2" data-testid={`regen-diff-row-${label.toLowerCase().replace(/\s+/g,"-")}`}>
      <div className="text-[11px]" style={{ color: "var(--muted)" }}>{L(label)}</div>
      <div className="text-right flex items-center gap-1.5">
        <span className="text-[10px]" style={{ color: "var(--muted)", textDecoration: hasAfter ? "line-through" : "none" }}>
          {_fmt(before, { prec, money, pct })}
        </span>
        {loading ? (
          <Loader2 size={10} className="animate-spin" style={{ color: "var(--muted)" }} />
        ) : hasAfter ? (
          <>
            <span className="text-xs font-mono font-bold" style={{ color: "var(--char)" }}>
              {_fmt(after, { prec, money, pct })}
            </span>
            {delta != null && deltaPct != null && (
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold inline-flex items-center gap-0.5"
                       style={{ background: chipBg, color: chipColor }}>
                <Icon size={9} />
                {isFlat ? "0" : `${delta > 0 ? "+" : ""}${pctFmt(deltaPct, 0)}`}
              </span>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};
